import { useQueryClient } from '@tanstack/react-query';

import { useError } from '#globalContexts/ErrorContext';
import { captureException } from '#lib/sentry';

import { validateCreditsForAction } from '../../credits/api/creditsValidation';
import { extendCompanySearch } from '../api/googleMapsScraperApi';
import { companiesKeys } from '../models/companies/keys';
import { prospectQueryKeys } from '../models/prospectQueries/keys';

/**
 * Hook to handle lead-related actions
 * Encapsulates complex business logic for lead operations
 */
export const useLeadActions = ({
	addLeadsToEnrichmentMutation,
	deleteLeadsMutation,
	abortSearchJobMutation,
	setDetailLeadId,
}) => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useError();

	/**
	 * Start contact enrichment for a company scope. Request failures surface through the
	 * global mutation error toast.
	 * @param {Object} actionScope - Result of useActionScope ({ scope, count, prospectQueryId })
	 * @param {Object} options - Contact criteria (contactRoles, maxContacts, preferredChannels)
	 */
	const handleStartEnrichment = async ({ scope, count, prospectQueryId }, options = {}) => {
		if (!prospectQueryId || count === 0) return;

		const creditValidation = await validateCreditsForAction('leadEnrichment', count);
		if (!creditValidation.hasEnoughCredits) {
			showError({ title: 'Nicht genügend Credits', message: creditValidation.error, autoClose: false });
			return;
		}

		addLeadsToEnrichmentMutation.mutate({
			scope,
			prospectQueryId,
			contactRoles: options.contactRoles || null,
			maxContacts: options.maxContacts || null,
			preferredChannels: options.preferredChannels?.length > 0 ? options.preferredChannels : null,
		});
	};

	/**
	 * Delete the companies of a scope from their search query. Request failures surface
	 * through the global mutation error toast.
	 * @param {Object} params
	 * @param {string} params.prospectQueryId
	 * @param {{leadIds: string[]} | {selectAll: true, filters?: Object}} params.scope
	 */
	const handleDeleteLeads = ({ prospectQueryId, scope }) => {
		deleteLeadsMutation.mutate(
			{ prospectQueryId, scope },
			{
				onSuccess: ({ excluded }) => {
					setDetailLeadId(null);
					showSuccess({
						title: 'Erfolgreich gelöscht',
						message: `${excluded} ${excluded === 1 ? 'Lead wurde' : 'Leads wurden'} erfolgreich gelöscht.`,
						autoClose: true,
						autoCloseDuration: 3000,
					});
				},
			},
		);
	};

	/**
	 * Extend a company search (Google Maps or OpenRegister) with additional results
	 */
	const handleExtendCompanySearch = async (
		selectedSearchQueryId,
		searchQueries,
		additionalResults,
		searchParameters = null,
	) => {
		if (!selectedSearchQueryId) {
			showError({
				title: 'Keine Suchanfrage ausgewählt',
				message: 'Bitte wählen Sie eine Suchanfrage aus.',
				autoClose: true,
			});
			return;
		}

		const query = searchQueries.find((q) => q.id === selectedSearchQueryId);
		// Check if query has results (via companyCount) or Apify data.
		// HR searches can always be extended (no companyCount/Apify data to check against).
		const isHrSearch = query?.queryTypes?.code === 'b2bheroCommercialRegisterSearch';
		if (!query || !(query.companyCount > 0 || query.apifyDatasetId || query.apifyRunId || isHrSearch)) {
			showError({
				title: 'Erweiterung nicht möglich',
				message:
					'Diese Suchanfrage kann nicht erweitert werden, da noch keine Ergebnisse vorhanden sind oder die Maps-Suche nicht die Voraussetzungen für die Erweiterung erfüllt.',
				autoClose: true,
			});
			return;
		}

		try {
			const creditAction = isHrSearch ? 'localHrSearch' : 'googleMapsSearch';
			const creditValidation = await validateCreditsForAction(creditAction, additionalResults);

			if (!creditValidation.hasEnoughCredits) {
				showError({
					title: 'Nicht genügend Credits',
					message: creditValidation.error,
					autoClose: false,
				});
				return;
			}

			const result = await extendCompanySearch(
				selectedSearchQueryId,
				additionalResults,
				searchParameters,
				isHrSearch ? 'hr' : 'maps',
			);

			if (result.jobId) {
				showSuccess({
					title: 'Suche erweitert!',
					message: `Es werden ${additionalResults} zusätzliche Unternehmen gesucht.`,
					autoClose: true,
					autoCloseDuration: 3000,
				});

				queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
			}

			return result;
		} catch (error) {
			captureException(error, {
				tags: {
					section: 'lead-overview',
					operation: 'extend-company-search',
				},
				extra: {
					prospectQueryId: selectedSearchQueryId,
					additionalResults,
				},
				level: 'error',
			});

			showError({
				title: 'Fehler beim Erweitern',
				message: 'Die Suche konnte nicht erweitert werden. Bitte versuchen Sie es später erneut.',
				autoClose: true,
			});

			throw error;
		}
	};

	/**
	 * Abort running search job (GoogleMaps or CommercialRegister)
	 * @param {string} jobId - Bull job ID
	 * @param {string} jobType - Job type ('googleMapsScraper' or 'localHrSearch')
	 */
	const handleAbortSearchJob = async (jobId, jobType) => {
		try {
			await abortSearchJobMutation.mutateAsync({ jobId, jobType });

			showSuccess({
				title: 'Suche abgebrochen',
				message: 'Die laufende Suche wurde gestoppt. Bereits gefundene Ergebnisse bleiben erhalten.',
				autoClose: true,
				autoCloseDuration: 3000,
			});

			// Invalidate queries to show partial results
			setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: companiesKeys.all });
			}, 1000);
		} catch (error) {
			// Error handling is done in the mutation's onError
			showError({
				title: 'Fehler beim Abbrechen',
				message: 'Die Suche konnte nicht abgebrochen werden. Bitte versuchen Sie es später erneut.',
				autoClose: true,
			});
		}
	};

	return {
		handleStartEnrichment,
		handleDeleteLeads,
		handleExtendCompanySearch,
		handleAbortSearchJob,
	};
};
